import * as React from 'react';
import {
  isKnown,
  MERGE_VARIABLE_LABELS,
  tokenSpans,
  type MergeVariable,
} from '@vendorops/shared';

import { cn } from '@/lib/utils';

/**
 * A text field that paints merge variables as chips instead of leaving them as
 * `{{double_braces}}` (PRD §F3).
 *
 * The value is still plain text — `{{vendor_name}}` is what gets typed, stored,
 * and merged, and nothing downstream changes. What changes is that the planner
 * stops reading punctuation. The braces are still there — the field is a real
 * `<textarea>`, and a planner can select a chip, delete half of it, or paste
 * one in from elsewhere, so it has to keep behaving like text when they do —
 * but they are drawn at zero opacity, so the bubble reads the way the message
 * body's chips do: a name, with no syntax around it. They reappear the moment
 * the planner selects across one, which is the moment the characters matter.
 *
 * The mechanism is an aligned mirror. A div behind the field renders the same
 * string with the chips drawn on it; the field itself is transparent-on-caret
 * so only the mirror is visible. That only holds while both layers lay text out
 * identically — so the chip styling is restricted to colour, radius, and a
 * spread shadow. Anything that changes an advance width (padding, margin,
 * font-weight, letter-spacing) would slide the mirror out from under the caret.
 */

/**
 * Layout-affecting classes. Both layers get these, verbatim, or they drift.
 *
 * The font family is stated rather than inherited: a bare `<textarea>` defaults
 * to the browser's monospace, and a mirror in the page font would sit a few
 * pixels off per character. `scrollbar-gutter` is here for the same reason —
 * where scrollbars take up space, one layer scrolling and the other not would
 * narrow the field's content box and wrap its text a word earlier.
 */
const METRICS =
  'font-sans text-sm leading-6 font-normal tracking-normal whitespace-pre-wrap break-words [scrollbar-gutter:stable]';

/**
 * `{{opening}}` is not a merge variable — `applyOpeningVariant` swaps it during
 * body assembly and it is deliberately absent from MERGE_VARIABLES. It is still
 * legitimate in a template body, so it gets its own colour rather than being
 * flagged as the typo it isn't.
 */
const OPENING = 'opening';

type ChipKind = 'variable' | 'opening' | 'unknown';

const CHIP_STYLES: Record<ChipKind, string> = {
  // The spread shadow gives the chip breathing room without occupying any, so
  // the mirror stays character-for-character aligned with the field.
  variable:
    'bg-[var(--color-primary-container)] text-[var(--color-accent-foreground)] shadow-[0_0_0_2px_var(--color-primary-container)]',
  opening:
    'bg-[var(--color-tertiary-container)] text-[#5c4a2e] shadow-[0_0_0_2px_var(--color-tertiary-container)]',
  unknown:
    'bg-destructive/10 text-destructive underline decoration-destructive/40 decoration-dotted underline-offset-2 shadow-[0_0_0_2px_rgba(170,55,28,0.10)]',
};

function chipKind(name: string): ChipKind {
  if (isKnown(name)) return 'variable';
  if (name === OPENING) return 'opening';
  return 'unknown';
}

/** The value, split into plain runs and chips, ready to render into the mirror. */
function mirror(value: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let cursor = 0;

  for (const span of tokenSpans(value)) {
    if (span.start > cursor) out.push(value.slice(cursor, span.start));
    out.push(
      <span
        key={span.start}
        className={cn(
          'rounded-full [box-decoration-break:clone] [-webkit-box-decoration-break:clone]',
          CHIP_STYLES[chipKind(span.name)],
        )}
      >
        {/* The braces stay in the text — a planner editing around a chip needs
            them to be where the characters actually are, and the mirror has to
            keep advancing exactly as the field beneath it does — but they are
            painted invisibly, so the four characters they occupy read as the
            bubble's padding instead of as syntax. This is what makes the
            subject agree with the message body, where a chip is a node and the
            braces are genuinely absent. */}
        <span className="opacity-0">{'{{'}</span>
        {span.name}
        <span className="opacity-0">{'}}'}</span>
      </span>,
    );
    cursor = span.end;
  }

  if (cursor < value.length) out.push(value.slice(cursor));
  // A trailing newline is collapsed by the mirror but not by the field, which
  // would leave the last line unscrollable. The zero-width space holds it open.
  out.push('​');
  return out;
}

/**
 * Which surface the field is drawn on.
 *
 * `field` is a standalone form control, dressed like every other input in the
 * app. `envelope` is a line of the letter the composer writes — the subject
 * line of a mail client is not a box, and a boxed input glued into a rendered
 * email reads as a form someone pasted into a letter. Same reasoning, and
 * deliberately the same vocabulary, as `VendorRecipientField`'s variant for
 * the To line directly beneath it.
 *
 * A prop rather than a `className` the caller passes in: the envelope
 * treatment has to *un-set* the border, the fill and the focus lift the
 * control variant is built from, and replace the focus indicator with one that
 * works without a box to lift. A caller handing in utilities would have to
 * know which of those to cancel, and the easy one to forget is the focus
 * indicator. An `outline` rather than a `ring`, because a ring is a box-shadow
 * and forced-colors mode wipes box-shadows.
 */
export type VariableFieldVariant = 'field' | 'envelope';

const SHELL_BASE =
  'relative isolate w-full overflow-hidden transition-[color,box-shadow,background-color,outline-color]';

const SHELL: Record<VariableFieldVariant, string> = {
  field:
    SHELL_BASE +
    ' rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)]' +
    ' focus-within:bg-[var(--color-surface-bright)] focus-within:shadow-(--shadow-input-focus)',

  // Letterhead: no outline, no fill, no shadow. The hover tint is the only
  // thing saying the line can be typed in before the caret is in it, and the
  // focus outline is `--color-ring` — the same indicator the To line below
  // uses, so the two live rows of the envelope agree about what focus is.
  envelope:
    SHELL_BASE +
    ' rounded-lg border border-transparent bg-transparent hover:bg-[var(--color-surface-container-low)]' +
    ' focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring',
};

/** The padding of the two dresses. On the envelope the band around the field
 *  already carries the letter's gutter, so the field itself sits tight — and
 *  keeps the weight the subject line is read at. Both the mirror and the real
 *  field take it, or the glyphs stop lining up with the caret. */
const SHELL_METRICS: Record<VariableFieldVariant, string> = {
  field: 'px-4 py-3',
  envelope: 'px-1.5 py-0.5 font-medium',
};

/** Transparent text plus a visible caret: the glyphs come from the mirror. */
const FIELD =
  'relative block w-full bg-transparent text-transparent caret-foreground outline-none ' +
  'selection:bg-primary/25 selection:text-foreground';

export function VariableTextarea({
  value,
  className,
  ref,
  onScroll,
  ...props
}: Omit<React.ComponentProps<'textarea'>, 'value'> & { value: string }) {
  const mirrorRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className={cn(SHELL.field, className)}>
      <div
        ref={mirrorRef}
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 overflow-hidden',
          METRICS,
          SHELL_METRICS.field,
        )}
      >
        {mirror(value)}
      </div>
      <textarea
        ref={ref}
        value={value}
        className={cn(FIELD, METRICS, SHELL_METRICS.field, 'resize-none overflow-auto')}
        onScroll={(e) => {
          if (mirrorRef.current) mirrorRef.current.scrollTop = e.currentTarget.scrollTop;
          onScroll?.(e);
        }}
        {...props}
      />
    </div>
  );
}

export function VariableInput({
  value,
  className,
  variant = 'field',
  ref,
  onScroll,
  ...props
}: Omit<React.ComponentProps<'input'>, 'value'> & {
  value: string;
  variant?: VariableFieldVariant;
}) {
  const mirrorRef = React.useRef<HTMLDivElement>(null);

  // A single-line field scrolls horizontally as the caret runs past the edge,
  // and the mirror has to follow it. `onScroll` alone misses caret-driven
  // scrolling in some browsers, so the field's own scrollLeft is read back on
  // every keystroke as well.
  const sync = (el: HTMLInputElement | null) => {
    if (el && mirrorRef.current) mirrorRef.current.scrollLeft = el.scrollLeft;
  };

  return (
    <div className={cn(SHELL[variant], className)}>
      <div
        ref={mirrorRef}
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 overflow-hidden',
          METRICS,
          SHELL_METRICS[variant],
          'whitespace-pre',
        )}
      >
        {mirror(value)}
      </div>
      <input
        ref={ref}
        value={value}
        className={cn(FIELD, METRICS, SHELL_METRICS[variant], 'whitespace-pre overflow-x-auto')}
        onScroll={(e) => {
          sync(e.currentTarget);
          onScroll?.(e);
        }}
        onKeyUp={(e) => sync(e.currentTarget)}
        onSelect={(e) => sync(e.currentTarget as HTMLInputElement)}
        {...props}
      />
    </div>
  );
}

/** What a chip is called when it is standing on its own rather than inline in a
 *  field — prose, the palette, an error. Free-standing chips can afford the
 *  human label and real padding; the ones drawn over live text cannot. */
export function variableLabel(name: string): string {
  if (name === OPENING) return 'Opening line';
  // An unknown name has no friendly label to give it, and inventing one would
  // hide the typo. It is shown as written so it can be found in the message.
  return MERGE_VARIABLE_LABELS[name as MergeVariable] ?? `{{${name}}}`;
}

/** The chip's colours, for a caller that draws its own chip — the rich body
 *  editor, whose chips are ProseMirror nodes rather than React elements and so
 *  cannot use `VariableChip` itself. Same colours, so "the green thing in my
 *  message" is one thing across the product. */
export function variableChipClass(name: string): string {
  return PROSE_CHIP[chipKind(name)];
}

const PROSE_CHIP: Record<ChipKind, string> = {
  variable: 'bg-[var(--color-primary-container)] text-[var(--color-accent-foreground)]',
  opening: 'bg-[var(--color-tertiary-container)] text-[#5c4a2e]',
  unknown: 'bg-destructive/10 text-destructive',
};

/**
 * A merge variable shown as itself — in prose, in the palette, in an error —
 * rather than as the token it becomes. Same colours as the chips drawn inside
 * the field, so "the green thing in my message" and "the green thing I clicked"
 * are visibly the same thing.
 */
export function VariableChip({
  name,
  className,
  children,
}: {
  name: string;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 align-baseline text-xs font-medium',
        PROSE_CHIP[chipKind(name)],
        className,
      )}
    >
      {children ?? variableLabel(name)}
    </span>
  );
}
